import { useCallback, useEffect, useRef, useState } from 'react';
import { showSearchAliases } from '../utils/showSearch';

// Own pending state above the drawer so closing it does not discard a save.
export default function useShowPreferenceMutations(saveStatus, onSaved) {
    const [mutations, setMutations] = useState({});
    const [savedShows, setSavedShows] = useState({});
    const mutationsRef = useRef({});
    const pendingRef = useRef(new Map());
    const mountedRef = useRef(true);
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const save = useCallback((show, status) => {
        const aliases = showSearchAliases(show);
        const pendingKey = aliases.find((key) => pendingRef.current.has(key));
        if (pendingKey) return pendingRef.current.get(pendingKey);
        const entries = (value, keys = aliases) => Object.fromEntries(keys.map((key) => [key, value]));
        const failedMutation = aliases.map((key) => mutationsRef.current[key])
            .find((mutation) => mutation?.error);
        // A read may observe the server's write before the save response fails.
        // Keep the displayed pre-save preference, including through Retry.
        const previousStatus = failedMutation ? failedMutation.previousStatus :
            (show.subscribeStatus === true || show.subscribeStatus === false ? show.subscribeStatus : null);
        const updateMutations = (patch) => {
            mutationsRef.current = { ...mutationsRef.current, ...patch };
            setMutations(mutationsRef.current);
        };
        updateMutations(entries({ saving: true, status, previousStatus }));
        const request = Promise.resolve().then(() => saveStatus(show, status)).then((result) => {
            if (!mountedRef.current) return result;
            const canonicalKey = `source:${result.source.id}`;
            setSavedShows((current) => ({ ...current, ...entries(result), [canonicalKey]: result }));
            updateMutations({ ...entries(null), [canonicalKey]: null });
            onSavedRef.current(result);
            return result;
        }).catch(() => {
            if (mountedRef.current) {
                updateMutations(entries({ saving: false, error: true, status, previousStatus }));
            }
        }).finally(() => {
            aliases.forEach((key) => pendingRef.current.delete(key));
        });
        aliases.forEach((key) => pendingRef.current.set(key, request));
        return request;
    }, [saveStatus]);

    const waitForSave = useCallback((show) => (
        showSearchAliases(show).map((key) => pendingRef.current.get(key)).find(Boolean)
    ), []);
    return { save, mutations, savedShows, waitForSave };
}
